"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Truck, CalendarClock, Snowflake, MapPin, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  title: string;
  description: string;
  badge?: string;
  linkTo: string;
  linkLabel: string;
  iconName: "Truck" | "CalendarClock" | "Snowflake" | "MapPin";
}
const serviceList: ServiceItem[] = [{
  id: "delivery",
  title: "Scheduled Delivery & Pickup",
  description: "Delivery, positioning, setup, and pickup are scheduled based on the rental dates, delivery location, site access, and availability.",
  badge: "Delivery and Pickup",
  linkTo: "/RefrigeratedTrailersPage",
  linkLabel: "See trailer lineup",
  iconName: "Truck"
}, {
  id: "terms",
  title: "Rental Terms",
  description: "Daily, weekly & Monthly rentals available. Availability and rental terms provided with quote.",
  badge: "Terms Provided with Quote",
  linkTo: "/RentalOptionsPricingPage",
  linkLabel: "View pricing details",
  iconName: "CalendarClock"
}, {
  id: "options",
  title: "Trailer options",
  description: "Choose from 6×8, 7×12 and 7×16 refrigerated and freezer trailers, plus a 53-foot reefer trailer for larger temporary cold-storage needs.",
  linkTo: "/RefrigeratedTrailersPage",
  linkLabel: "Explore trailer options",
  iconName: "Snowflake"
}, {
  id: "coverage",
  title: "Regional coverage",
  description: "Serving Southern California and our listed Las Vegas-area communities.",
  badge: "CA & NV service",
  linkTo: "/ServiceAreasPage",
  linkLabel: "View Service Areas",
  iconName: "MapPin"
}];
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.07
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="service-categories" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border/60">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/30 text-accent-foreground text-xs font-semibold uppercase tracking-wider mb-3">
              <span className="size-2 rounded-full bg-accent-foreground inline-block" />
              Service categories
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
              Mobile Refrigerated and Freezer Trailer Rentals
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Mobile refrigerated and freezer trailer rentals with delivery and pickup service throughout Southern California and our listed Las Vegas-area communities.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild variant="default" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestAQuotePage">Request a Quote</Link>
            </Button>
            <a href="tel:2135218821" className="inline-flex items-center justify-center sm:justify-start gap-2 text-sm font-medium text-muted-foreground py-1 sm:py-0">
              <span>213-521-8821</span>
            </a>
          </div>
        </div>

        {/* 4-Column Service Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-10">
          {serviceList.map((service, index) => {
          return <motion.div key={service.id} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full flex flex-col justify-between bg-card text-card-foreground border border-border rounded-lg p-6 shadow-xs hover:border-primary/40 hover:-translate-y-1 transition-[border-color,box-shadow,transform] duration-200">
                  <div>
                    {/* Top row: Icon container and status tag */}
                    <div className="flex items-center justify-between gap-3 mb-5">
                      <div className="size-10 rounded-lg bg-accent/40 text-accent-foreground flex items-center justify-center border border-border">
                        {service.iconName === "Truck" && <Truck className="size-5 stroke-[1.75]" />}
                        {service.iconName === "CalendarClock" && <CalendarClock className="size-5 stroke-[1.75]" />}
                        {service.iconName === "Snowflake" && <Snowflake className="size-5 stroke-[1.75]" />}
                        {service.iconName === "MapPin" && <MapPin className="size-5 stroke-[1.75]" />}
                      </div>
                      {service.badge && <span className="text-xs font-medium px-2.5 py-1 rounded bg-muted text-muted-foreground border border-border/80">
                          {service.badge}
                        </span>}
                    </div>

                    <CardHeader className="p-0 mb-3">
                      <CardTitle className="text-xl font-bold font-default text-card-foreground">
                        <h3>{service.title}</h3>
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="p-0">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                    </CardContent>
                  </div>

                  <CardFooter className="p-0 pt-6 mt-6 border-t border-border/60">
                    <Link to={service.linkTo} className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-primary transition-colors group">
                      <span>{service.linkLabel}</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Context Banner */}
        <div className="mt-12 rounded-lg bg-muted/40 border border-border p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="size-2.5 rounded-full bg-primary shrink-0" />
            <p className="text-sm text-foreground font-medium">
              Request a quote for mobile refrigerated or freezer trailer rental availability in Southern California or our listed Las Vegas-area communities.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            <Button asChild variant="outline" size="sm" className="w-full sm:w-auto border-border">
              <Link to="/RentalOptionsPricingPage">Compare rental options</Link>
            </Button>
            <Button asChild variant="default" size="sm" className="w-full sm:w-auto bg-primary text-primary-foreground">
              <Link to="/ContactRequestAQuotePage">Request a Quote</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}